import type {
  EditableGridCellDisplay,
  EditableGridCellNullableRef,
  EditableGridColumn,
  EditableGridEditorState,
  EditableGridRowPatchResult,
  EditableGridSuggestCreateContext,
  EditableGridSuggestOption,
} from './types';

type CellDisplayOptions = {
  dimmed?: boolean;
  linked?: boolean;
  emptyText?: string;
};

type CellDisplayInput = EditableGridCellDisplay | string | number | null | undefined;

function isCellDisplay(value: CellDisplayInput): value is EditableGridCellDisplay {
  return typeof value === 'object' && value != null && 'text' in value;
}

export function toCellDisplay(
  value: string | number | null | undefined,
  options?: CellDisplayOptions
): EditableGridCellDisplay {
  const text = value == null ? '' : String(value);
  return {
    text: text || options?.emptyText || '',
    dimmed: options?.dimmed,
    linked: options?.linked,
  };
}

export function parseNumber(value: unknown, fallback: number): number {
  const text = String(value ?? '')
    .trim()
    .replace(',', '.');
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function parseNullableNumber(value: unknown): number | null {
  const text = String(value ?? '')
    .trim()
    .replace(',', '.');
  if (!text) return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}

type TextCellPatchContext<Row> = {
  row: Row;
  value: string;
  previousValue: string;
};

export type TextCellConfig<
  Row,
  ApiPatch extends Record<string, unknown>,
  ColumnKey extends string,
> = {
  key: ColumnKey;
  title: string;
  width?: number;
  placeholder?: string;
  multiline?: boolean | 'auto';
  dimmed?: boolean;
  editable?: boolean;
  persist?: boolean;
  /**
   * Резолвер значения: для отображения и редактора. По умолчанию row[key].
   */
  value?: (row: Row) => string | null | undefined;
  /**
   * Кастомный сборщик патча (suggest, нестандартная нормализация). По умолчанию trim + { [key]: value }.
   */
  onPatch?: (ctx: TextCellPatchContext<Row>) => EditableGridRowPatchResult<Row, ApiPatch>;
};

function getByKey(row: unknown, key: string): unknown {
  return (row as Record<string, unknown>)[key];
}

function makePatchResult<Row, ApiPatch extends Record<string, unknown>>(
  key: string,
  next: unknown,
  prev: unknown
): EditableGridRowPatchResult<Row, ApiPatch> {
  const hasChanges = !Object.is(next, prev);
  return {
    hasChanges,
    apiPatch: { [key]: next } as ApiPatch,
    localPatch: { [key]: next } as Partial<Row>,
  };
}

function normalizePrecision(precision?: number): number | undefined {
  if (precision == null || !Number.isFinite(precision)) return undefined;
  const normalized = Math.max(0, Math.floor(precision));
  return normalized;
}

function applyPrecision(value: number | null, precision?: number): number | null {
  if (value == null || precision == null) return value;
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
}

function normalizeBound(value?: number): number | undefined {
  if (value == null || !Number.isFinite(value)) return undefined;
  return value;
}

function clampValue(value: number | null, minValue?: number, maxValue?: number): number | null {
  if (value == null) return value;

  const min = normalizeBound(minValue);
  const max = normalizeBound(maxValue);

  if (min == null && max == null) return value;

  if (min != null && max != null) {
    const low = Math.min(min, max);
    const high = Math.max(min, max);
    return Math.min(Math.max(value, low), high);
  }

  if (min != null) return Math.max(value, min);
  return Math.min(value, max as number);
}

/**
 * Creates a text column for EditableGrid.
 *
 * Defaults:
 * - `value`: row[key]
 * - `onPatch`: trim + { [key]: value }
 */
export function TextCell<Row, ApiPatch extends Record<string, unknown>, ColumnKey extends string>(
  config: TextCellConfig<Row, ApiPatch, ColumnKey>
): EditableGridColumn<Row, ApiPatch, ColumnKey> {
  const getValue =
    config.value ??
    ((row: Row) => {
      const raw = getByKey(row, config.key);
      return raw == null ? null : String(raw);
    });

  const buildPatch =
    config.onPatch ??
    ((ctx: TextCellPatchContext<Row>) => {
      const next = ctx.value.trim() || null;
      const prev = ctx.previousValue.trim() || null;
      return makePatchResult<Row, ApiPatch>(String(config.key), next, prev);
    });

  return {
    key: config.key,
    title: config.title,
    width: config.width,
    placeholder: config.placeholder,
    multiline: config.multiline ?? false,
    editable: config.editable,
    persist: config.persist,
    kind: 'text',
    value: config.value,
    getDisplayFromRow: row => toCellDisplay(getValue(row), { dimmed: config.dimmed }),
    getEditorFromRow: row => ({ value: getValue(row) ?? '' }),
    buildRowPatch: (row, editor) =>
      buildPatch({
        row,
        value: editor.value,
        previousValue: getValue(row) ?? '',
      }),
  };
}

type NumberCellPatchContext<Row> = {
  row: Row;
  raw: string;
  value: number | null;
  previousValue: number | null;
};

export type NumberCellConfig<
  Row,
  ApiPatch extends Record<string, unknown>,
  ColumnKey extends string,
> = {
  key: ColumnKey;
  title: string;
  width: number;
  placeholder?: string;
  editable?: boolean;
  persist?: boolean;
  /**
   * Резолвер значения. По умолчанию row[key].
   */
  value?: (row: Row) => number | null | undefined;
  fallback?: number;
  precision?: number;
  minValue?: number;
  maxValue?: number;
  nullable?: boolean;
  parse?: (raw: string) => number | null;
  formatDisplay?: (value: number) => string;
  onPatch?: (ctx: NumberCellPatchContext<Row>) => EditableGridRowPatchResult<Row, ApiPatch>;
};

/**
 * Creates a number column for EditableGrid.
 *
 * Defaults:
 * - `value`: row[key]
 * - `fallback`: 0, `nullable`: false
 * - `onPatch`: { [key]: value }
 */
export function NumberCell<Row, ApiPatch extends Record<string, unknown>, ColumnKey extends string>(
  config: NumberCellConfig<Row, ApiPatch, ColumnKey>
): EditableGridColumn<Row, ApiPatch, ColumnKey> {
  const precision = normalizePrecision(config.precision);
  const fallback = Number.isFinite(config.fallback as number) ? Number(config.fallback) : 0;
  const nullable = config.nullable ?? false;

  const normalizeValue = (value: number | null): number | null =>
    clampValue(applyPrecision(value, precision), config.minValue, config.maxValue);

  const getValue =
    config.value ??
    ((row: Row) => {
      const raw = getByKey(row, config.key);
      if (raw == null) return null;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? normalizeValue(parsed) : null;
    });

  const parse =
    config.parse ??
    ((raw: string) => {
      if (nullable) return parseNullableNumber(raw);
      return parseNumber(raw, fallback);
    });

  const format =
    config.formatDisplay ??
    ((value: number) => {
      if (precision == null) return value.toLocaleString();
      return value.toLocaleString(undefined, {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      });
    });

  const buildPatch =
    config.onPatch ??
    ((ctx: NumberCellPatchContext<Row>) => {
      const key = String(config.key);
      const nextPatch = { [key]: ctx.value } as Record<string, unknown>;
      const hasBaseChanges = !Object.is(ctx.value, ctx.previousValue);
      return {
        hasChanges: hasBaseChanges,
        apiPatch: nextPatch as ApiPatch,
        localPatch: nextPatch as Partial<Row>,
      };
    });

  return {
    key: config.key,
    title: config.title,
    width: config.width,
    placeholder: config.placeholder,
    editable: config.editable,
    persist: config.persist,
    kind: 'number',
    value: config.value,
    getDisplayFromRow: row => {
      const value = getValue(row);
      return toCellDisplay(value == null ? '' : format(Number(value)));
    },
    getEditorFromRow: row => {
      const value = getValue(row);
      if (value == null) return { value: '' };
      if (precision == null) return { value: String(value) };
      return { value: Number(value).toFixed(precision) };
    },
    buildRowPatch: (row, editor) => {
      const previousRaw = getValue(row);
      const previousValue = previousRaw == null ? null : Number(previousRaw);
      const parsedValue = parse(editor.value);
      const value = normalizeValue(parsedValue == null ? null : Number(parsedValue));
      return buildPatch({
        row,
        raw: editor.value,
        value,
        previousValue,
      });
    },
  };
}

type MoneyCellPatchContext<Row> = NumberCellPatchContext<Row>;

export type MoneyCellConfig<
  Row,
  ApiPatch extends Record<string, unknown>,
  ColumnKey extends string,
> = {
  key: ColumnKey;
  title: string;
  width: number;
  placeholder?: string;
  editable?: boolean;
  /**
   * Резолвер значения (display/editor; при persist — пишется в patch по key). По умолчанию row[key].
   */
  value?: (row: Row) => number | null | undefined;
  fallback?: number;
  precision?: number;
  minValue?: number;
  maxValue?: number;
  parse?: (raw: string) => number | null;
  formatDisplay?: (value: number) => string;
  onPatch?: (ctx: MoneyCellPatchContext<Row>) => EditableGridRowPatchResult<Row, ApiPatch>;
  /** При true контроллер дописывает value(nextRow) в patch. */
  persist?: boolean;
};

export function formatMoney(value: number): string {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Creates a money column for EditableGrid.
 *
 * Defaults:
 * - `value`: row[key]
 * - `fallback`: 0, `precision`: 2
 * - `onPatch`: { [key]: value }
 */
export function MoneyCell<Row, ApiPatch extends Record<string, unknown>, ColumnKey extends string>(
  config: MoneyCellConfig<Row, ApiPatch, ColumnKey>
): EditableGridColumn<Row, ApiPatch, ColumnKey> {
  const precision = normalizePrecision(config.precision) ?? 2;
  const fallback = Number.isFinite(config.fallback as number) ? Number(config.fallback) : 0;

  const normalizeValue = (value: number | null): number | null =>
    clampValue(applyPrecision(value, precision), config.minValue, config.maxValue);

  const getValue =
    config.value ??
    ((row: Row) => {
      const raw = getByKey(row, config.key);
      if (raw == null) return null;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? normalizeValue(parsed) : null;
    });

  const parse = config.parse ?? ((raw: string) => parseNumber(raw, fallback));
  const format =
    config.formatDisplay ??
    ((value: number) =>
      value.toLocaleString(undefined, {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      }));

  const buildPatch =
    config.onPatch ??
    ((ctx: MoneyCellPatchContext<Row>) => {
      const key = String(config.key);
      const nextPatch = { [key]: ctx.value } as Record<string, unknown>;
      const hasBaseChanges = !Object.is(ctx.value, ctx.previousValue);
      return {
        hasChanges: hasBaseChanges,
        apiPatch: nextPatch as ApiPatch,
        localPatch: nextPatch as Partial<Row>,
      };
    });

  return {
    key: config.key,
    title: config.title,
    width: config.width,
    placeholder: config.placeholder,
    editable: config.editable,
    value: config.value,
    persist: config.persist,
    kind: 'money',
    getDisplayFromRow: row => {
      const value = getValue(row);
      return toCellDisplay(value == null ? '' : format(Number(value)));
    },
    getEditorFromRow: row => {
      const value = getValue(row);
      return { value: value == null ? '' : Number(value).toFixed(precision) };
    },
    buildRowPatch: (row, editor) => {
      const previousRaw = getValue(row);
      const previousValue = previousRaw == null ? null : Number(previousRaw);
      const parsedValue = parse(editor.value);
      const value = normalizeValue(parsedValue == null ? null : Number(parsedValue));
      return buildPatch({
        row,
        raw: editor.value,
        value,
        previousValue,
      });
    },
  };
}

type SuggestionCellPatchContext<Row> = EditableGridSuggestCreateContext<Row>;

export type SuggestionCellConfig<
  Row,
  ApiPatch extends Record<string, unknown>,
  ColumnKey extends string,
> = {
  key: ColumnKey;
  title: string;
  width?: number;
  placeholder?: string;
  placeholderValue?: string;
  editable?: boolean;
  persist?: boolean;
  minChars?: number;
  debounceMs?: number;
  getDisplayFromRow: (row: Row) => CellDisplayInput;
  getEditorFromRow: (row: Row) => EditableGridEditorState;
  onSearch: (query: string) => Promise<EditableGridSuggestOption[]>;
  onPatch: (ctx: SuggestionCellPatchContext<Row>) => EditableGridRowPatchResult<Row, ApiPatch>;
  onCreate?: (ctx: EditableGridSuggestCreateContext<Row>) => Promise<EditableGridCellNullableRef>;
  onApplyOption?: (
    option: EditableGridSuggestOption,
    current: EditableGridEditorState
  ) => EditableGridEditorState;
  onEditorValueChange?: (
    current: EditableGridEditorState,
    nextValue: string
  ) => EditableGridEditorState;
  getSelectedRefFromRow?: (row: Row) => EditableGridCellNullableRef;
  parseSelectedRef?: (raw: unknown) => EditableGridCellNullableRef;
  selectedMetaKey?: string;
};

/**
 * Creates a suggestion/autocomplete column for EditableGrid.
 *
 * Unlike primitive cells, suggestion behavior is typically domain-specific,
 * so `onSearch` and `onPatch` are required.
 */
export function SuggestionCell<
  Row,
  ApiPatch extends Record<string, unknown>,
  ColumnKey extends string,
>(
  config: SuggestionCellConfig<Row, ApiPatch, ColumnKey>
): EditableGridColumn<Row, ApiPatch, ColumnKey> {
  const metaKey = config.selectedMetaKey ?? 'selectedRef';

  const resolveDisplay = (input: CellDisplayInput): EditableGridCellDisplay =>
    isCellDisplay(input) ? input : toCellDisplay(input);

  const parseSelectedRef =
    config.parseSelectedRef ??
    ((raw: unknown): EditableGridCellNullableRef => {
      if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!trimmed) return null;
        const parsedNumber = Number(trimmed);
        if (Number.isFinite(parsedNumber) && String(parsedNumber) === trimmed) return parsedNumber;
        return trimmed;
      }
      return null;
    });

  const getSelectedRefFromEditor = (editor: EditableGridEditorState): EditableGridCellNullableRef =>
    parseSelectedRef(editor.meta?.[metaKey]);

  const applyOption =
    config.onApplyOption ??
    (option => ({
      value: option.label,
      meta: { [metaKey]: option.id },
    }));

  const onEditorValueChange =
    config.onEditorValueChange ??
    ((current, nextValue) => ({
      value: nextValue,
      meta: { ...(current.meta ?? {}), [metaKey]: null },
    }));

  const buildCreateContext = (
    row: Row,
    editor: EditableGridEditorState
  ): EditableGridSuggestCreateContext<Row> => {
    const currentEditor = config.getEditorFromRow(row);
    const selectedRef = getSelectedRefFromEditor(editor);
    const previousSelectedRef =
      config.getSelectedRefFromRow?.(row) ?? getSelectedRefFromEditor(currentEditor);

    return {
      row,
      value: editor.value,
      previousValue: currentEditor.value,
      selectedRef,
      previousSelectedRef,
      editor,
    };
  };

  return {
    key: config.key,
    title: config.title,
    width: config.width,
    placeholder: config.placeholder,
    placeholderValue: config.placeholderValue,
    editable: config.editable,
    persist: config.persist,
    kind: 'suggestion',
    getDisplayFromRow: row => resolveDisplay(config.getDisplayFromRow(row)),
    getEditorFromRow: config.getEditorFromRow,
    onEditorValueChange,
    buildRowPatch: (row, editor) => config.onPatch(buildCreateContext(row, editor)),
    suggest: {
      minChars: config.minChars,
      debounceMs: config.debounceMs,
      search: config.onSearch,
      applyOption,
      buildCreateContext: (row, editor) =>
        buildCreateContext(row as Row, editor) as EditableGridSuggestCreateContext<unknown>,
      create: config.onCreate
        ? ctx => config.onCreate!(ctx as EditableGridSuggestCreateContext<Row>)
        : undefined,
    },
  };
}

export type CheckboxCellConfig<
  Row,
  ApiPatch extends Record<string, unknown>,
  ColumnKey extends string,
> = {
  key: ColumnKey;
  title: string;
  width?: number;
  editable?: boolean;
  /** Получить boolean-значение из строки. */
  getValue: (row: Row) => boolean;
  /** Построить патч при toggle (checked — новое значение после toggle). */
  buildTogglePatch: (
    row: Row,
    checked: boolean
  ) => {
    apiPatch: Partial<ApiPatch>;
    localPatch?: Partial<Row>;
  };
};

/**
 * Creates a checkbox column for EditableGrid.
 *
 * Checkbox работает через мгновенный toggle без текстового редактора.
 * buildRowPatch инвертирует текущее значение и вызывает buildTogglePatch.
 */
export function CheckboxCell<
  Row,
  ApiPatch extends Record<string, unknown>,
  ColumnKey extends string,
>(
  config: CheckboxCellConfig<Row, ApiPatch, ColumnKey>
): EditableGridColumn<Row, ApiPatch, ColumnKey> {
  return {
    key: config.key,
    title: config.title,
    width: config.width,
    editable: config.editable,
    kind: 'checkbox',
    getDisplayFromRow: row => toCellDisplay(config.getValue(row) ? '✓' : ''),
    getEditorFromRow: row => ({ value: config.getValue(row) ? 'true' : 'false' }),
    buildRowPatch: row => {
      const current = config.getValue(row);
      const next = !current;
      const patch = config.buildTogglePatch(row, next);
      return {
        hasChanges: true,
        apiPatch: patch.apiPatch as ApiPatch,
        localPatch: patch.localPatch,
      };
    },
  };
}

/**
 * Creates a typed column factory bound to specific Row, ApiPatch, and ColumnKey types.
 * Eliminates the need to specify generic parameters on each cell call.
 *
 * @example
 * const col = createColumnFactory<MyRow, MyPatch, MyColumnKey>();
 * const columns = [
 *   col.text({ key: 'name', title: 'Name' }),
 *   col.number({ key: 'qty', title: 'Qty', width: 80 }),
 *   col.money({ key: 'price', title: 'Price', width: 100 }),
 * ];
 */
export function createColumnFactory<
  Row,
  ApiPatch extends Record<string, unknown>,
  ColumnKey extends string,
>() {
  return {
    text: (config: TextCellConfig<Row, ApiPatch, ColumnKey>) =>
      TextCell<Row, ApiPatch, ColumnKey>(config),
    number: (config: NumberCellConfig<Row, ApiPatch, ColumnKey>) =>
      NumberCell<Row, ApiPatch, ColumnKey>(config),
    money: (config: MoneyCellConfig<Row, ApiPatch, ColumnKey>) =>
      MoneyCell<Row, ApiPatch, ColumnKey>(config),
    suggestion: (config: SuggestionCellConfig<Row, ApiPatch, ColumnKey>) =>
      SuggestionCell<Row, ApiPatch, ColumnKey>(config),
    checkbox: (config: CheckboxCellConfig<Row, ApiPatch, ColumnKey>) =>
      CheckboxCell<Row, ApiPatch, ColumnKey>(config),
  };
}
